import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { AIBox } from '@crazyegginc/hatch';

import { useSelectedSite, useCrazyAI, useAuthContext, useUserLanguage } from '/src/hooks';
import { recordingsDataForAIQuery } from '../../queries';
import { PlayButton } from '/src/components/PlayButton';

export function AISuggestions({ filtersHook }) {
  const { selectedSite } = useSelectedSite();
  const { initialLoadComplete, queryParams } = filtersHook;

  const { data } = useQuery({
    ...recordingsDataForAIQuery({
      siteId: selectedSite?.id,
      filters: JSON.stringify(queryParams.filters),
    }),
    enabled: Boolean(selectedSite?.id && initialLoadComplete),
  });

  const location = useLocation();
  const { currentUser } = useAuthContext();
  const userPreferredLanguage = useUserLanguage();

  const params = useMemo(
    () => ({
      data: data?.recordingsDataForAi,
      language: userPreferredLanguage,
    }),
    [data, userPreferredLanguage],
  );

  const context = useMemo(
    () => ({
      url: `${location.pathname}${location.search}`,
      userId: currentUser?.userId,
    }),
    [location.pathname, location.search, currentUser?.userId],
  );

  const minimumRecordingsExist = useMemo(() => {
    try {
      const raw = data?.recordingsDataForAi ?? '[]';
      const arr = JSON.parse(raw);
      return Array.isArray(arr) && arr.length >= 10;
    } catch {
      return false;
    }
  }, [data?.recordingsDataForAi]);

  const {
    data: aiData,
    error,
    refetch,
    positiveFeedback,
    negativeFeedback,
  } = useCrazyAI({
    type: 'recording_suggestions',
    params,
    context,
    enabled: minimumRecordingsExist,
  });

  const suggestions = useMemo(() => {
    if (aiData?.ready) {
      try {
        const parsed = JSON.parse(aiData.text);
        return (
          <>
            <div className="text-header-2 mb-1">Recordings You Should Watch:</div>
            {parsed.map((x) => (
              <div className="mb-2 flex" key={x.recording_id}>
                <PlayButton hashedId={x.recording_id} valid={true} sizeClass="h-6 w-6" />
                <span className="ml-3 leading-tight text-body-2">{x.reason}</span>
              </div>
            ))}
          </>
        );
      } catch {
        return { error: true };
      }
    }
    return { ready: false };
  }, [aiData]);

  return (
    <div className="mb-10">
      {minimumRecordingsExist ? (
        <AIBox
          size="lg"
          border={true}
          id="recording_suggestions"
          loading={!aiData?.ready}
          error={error || suggestions?.error}
          selectedFeedback={data?.feedback}
          refetch={refetch}
          onPositive={positiveFeedback}
          onNegative={negativeFeedback}
          showChangeLanguage={true}
        >
          {suggestions}
        </AIBox>
      ) : null}
    </div>
  );
}
